/**
 * API functions for the Wonder Navigation components
 * These will be used for address search and coverage check functionality
 */
import { transformedPackages } from '../../pages/PackagesPage/packagesData';

// API base URL for Cloudflare Functions
const API_BASE_URL = '/api';

// Minimum characters required for search
const MIN_CHARS = 4;

// For cancelling in-flight requests
let currentController = null;

/**
 * Search for addresses using the autocomplete API
 * @param {string} query - The search query
 * @returns {Promise<Object>} - The search results
 */
export const searchAddress = async (query) => {
  // Return empty results if query is too short
  if (!query || query.length < MIN_CHARS) {
    return { results: [] };
  }
  
  // Cancel any in-flight request
  if (currentController) {
    currentController.abort();
  }
  
  // Create a new AbortController for this request
  currentController = new AbortController();
  const signal = currentController.signal;
  
  try {
    // Fetch autocomplete suggestions using Cloudflare Function
    const response = await fetch(
      `${API_BASE_URL}/autocomplete?input=${encodeURIComponent(query)}`,
      { signal }
    );
    
    if (!response.ok) {
      throw new Error(`Error: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Transform the API response to match our expected format
    if (data && data.predictions) {
      return {
        results: data.predictions.map(prediction => ({
          id: prediction.place_id,
          mainText: prediction.structured_formatting?.main_text || prediction.description,
          secondaryText: prediction.structured_formatting?.secondary_text || '',
          placeId: prediction.place_id,
          description: prediction.description
        }))
      };
    } else {
      console.error('Unexpected response format:', data);
      return { results: [] };
    }
  } catch (error) {
    if (error.name === 'AbortError') {
      // Request was aborted, not an error
      return { results: [] };
    }
    console.error('Error fetching suggestions:', error);
    return { results: [] };
  }
};

/**
 * Get place details from place ID
 * @param {string} placeId - The place ID
 * @returns {Promise<Object>} - The place details
 */
export const getPlaceDetails = async (placeId) => {
  try {
    // Note: The parameter name is place_id, not placeid
    const response = await fetch(`${API_BASE_URL}/place-details?place_id=${encodeURIComponent(placeId)}`);
    
    if (!response.ok) {
      throw new Error(`Error: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Debug: Log the response data
    console.log('Place details response:', data);
    
    // Return the place details
    if (data && data.result && data.result.geometry && data.result.geometry.location) {
      return data.result;
    } else {
      console.error('Unexpected response format:', data);
      throw new Error('Invalid place details response');
    }
  } catch (error) {
    console.error('Error fetching place details:', error);
    throw error;
  }
};

/**
 * Check coverage for a location
 * @param {Object} coordinates - The coordinates object with lat and lng properties
 * @returns {Promise<Object>} - The coverage results
 */
export const checkCoverage = async (coordinates) => {
  try {
    // Convert coordinates to strings if they're not already
    const latitude = typeof coordinates.lat === 'string' ? coordinates.lat : coordinates.lat.toString();
    const longitude = typeof coordinates.lng === 'string' ? coordinates.lng : coordinates.lng.toString();
    
    console.log(`Checking coverage for coordinates: ${latitude}, ${longitude}`);
    
    const response = await fetch(`${API_BASE_URL}/coverage`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        latitude,
        longitude
      })
    });
    
    if (!response.ok) {
      throw new Error(`Error: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Debug: Log the response data
    console.log('Coverage response:', data);
    
    // Process the coverage data
    if (data && data.length > 0 && data[0].ObjectFound && data[0].ShapesFound.length > 0) {
      // Extract the specific fields we need and normalize operator names
      const coverageResults = data[0].ShapesFound.map(shape => {
        // Normalize operator names
        let operatorName = shape.OperatorName;
        let connectionType = 'FTTH'; // Default to FTTH
        
        // Extract connection type (FTTH or FTTB)
        // Check for explicit FTTB indicators first
        if (shape.NetworkType && (
            shape.NetworkType.toLowerCase().includes('fttb') ||
            shape.NetworkType.toLowerCase().includes('business') ||
            shape.NetworkType.toLowerCase().includes('office') ||
            shape.NetworkType.toLowerCase().includes('sme')
        )) {
          connectionType = 'FTTB';
        } else if (
            operatorName.toLowerCase().includes('fttb') ||
            operatorName.toLowerCase().includes('business') ||
            operatorName.toLowerCase().includes('office') ||
            operatorName.toLowerCase().includes('sme')
        ) {
          connectionType = 'FTTB';
        }
        
        // Normalize Vumatel variants
        if (operatorName.toLowerCase().includes('vumatel') ||
            operatorName.toLowerCase().includes('vuma')) {
          operatorName = 'Vumatel';
        }
        
        // Normalize Openserve variants
        if (operatorName.toLowerCase().includes('openserve')) {
          operatorName = 'Openserve';
        }
        
        // Create a combined name that includes the connection type for display
        const displayName = connectionType === 'FTTB' ?
          `${operatorName} Business` : operatorName;
        
        return {
          operatorName: operatorName,
          displayName: displayName,
          connectionType: connectionType,
          statusType: shape.StatusType,
          propertyName: shape.PropertyName,
          networkType: shape.NetworkType,
          mapKey: shape.MapKey || ''
        };
      });
      
      // Remove duplicates based on normalized operator name and network type
      const uniqueResults = [];
      const seen = new Set();
      
      coverageResults.forEach(result => {
        // Create a unique key for each result that includes connection type
        const key = `${result.operatorName.toLowerCase()}-${result.connectionType.toLowerCase()}`;
        
        // Only add if we haven't seen this combination before
        if (!seen.has(key)) {
          seen.add(key);
          uniqueResults.push(result);
        }
      });
      
      // Filter for live status if available
      const liveResults = uniqueResults.filter(result =>
        result.statusType.toLowerCase() === 'live'
      );
      
      // Use live results if available, otherwise use all unique results
      const finalResults = liveResults.length > 0 ? liveResults : uniqueResults;
      
      console.log('Original results:', coverageResults.length, 'Unique results:', uniqueResults.length);
      
      return {
        status: 'available',
        message: 'Great news! High-speed fibre is available at your address.',
        details: 'The following networks are available at your location:',
        coverageResults: finalResults
      };
    } else {
      return {
        status: 'not_available',
        message: 'We\'re not in your area yet.',
        details: 'Unfortunately, we don\'t have coverage in your area yet. We\'re expanding rapidly, so check back soon.',
        alternatives: ['LTE', 'Wireless']
      };
    }
  } catch (error) {
    console.error('Error checking coverage:', error);
    throw error;
  }
};

/**
 * Format address for display
 * @param {string} address - The address to format
 * @returns {string} - The formatted address
 */
export const formatAddress = (address) => {
  if (!address) return '';
  
  // Convert to uppercase for display
  return address.toUpperCase();
};

// Network operator mapping - using the correct logos from the old Navigation component
export const networkOperatorMap = {
  'centurycity': {
    name: 'Century City Connect',
    logo: 'https://apextech.group/century-city-connect-colour.png',
    color: '#0066cc',
    description: 'High-speed fiber in Century City area'
  },
  'frogfoot': {
    name: 'Frogfoot',
    logo: 'https://apextech.group/frogfoot-bw.png',
    color: '#00a651',
    description: 'Reliable fiber network across South Africa'
  },
  'metrofibre': {
    name: 'Metro Fibre',
    logo: 'https://apextech.group/mfn-colour.png',
    color: '#e31937',
    description: 'Fast and reliable fiber connectivity'
  },
  'octotel': {
    name: 'Octotel',
    logo: 'https://apextech.group/octotel-colour.png',
    color: '#ff5800',
    description: 'Premium fiber network in Cape Town'
  },
  'octotelbus': {
    name: 'Octotel Business',
    logo: 'https://apextech.group/octotel-colour.png',
    color: '#ff5800',
    description: 'Business-grade fiber solutions'
  },
  'openfibre': {
    name: 'Open Fibre',
    logo: 'https://apextech.group/OF_FullCol_white_landscape.png',
    color: '#00b0f0',
    description: 'Open access fiber network'
  },
  'openserve': {
    name: 'Openserve',
    logo: 'https://apextech.group/openserve-colour.png',
    color: '#7030a0',
    description: "Telkom's fiber network division"
  },
  'seacom': {
    name: 'Seacom',
    logo: 'https://apextech.group/seacom-bw.png',
    color: '#0078d7',
    description: 'Pan-African telecommunications provider'
  },
  'vumatelcc': {
    name: 'Vumatel',
    logo: 'https://apextech.group/vuma-colour.png',
    color: '#e41f35',
    description: "South Africa's largest fiber provider"
  },
  'vumatelct': {
    name: 'Vumatel Tactical',
    logo: 'https://apextech.group/vuma-colour.png',
    color: '#e41f35',
    description: "Tactical fiber solutions by Vumatel"
  },
  'lightwire': {
    name: 'Lightwire',
    logo: 'https://apextech.group/lightwire-colour.png',
    color: '#00b0f0',
    description: 'High-speed fiber connectivity'
  }
};

// Get network operator details from mapkey
export const getNetworkOperator = (mapKey) => {
  if (!mapKey) return null;
  
  // Convert to lowercase and remove any spaces
  let normalizedKey = mapKey.toLowerCase().replace(/\s+/g, '');
  
  // Normalize the mapKey first (handle Vumatel variants)
  if (normalizedKey.includes('vuma') || normalizedKey.includes('vumatel')) {
    normalizedKey = 'vumatel';
  }
  
  // Direct mappings for specific cases
  const directMappings = {
    'vuma': 'vumatelcc',
    'vumatel': 'vumatelcc', // All Vumatel variants map to the same logo
    'metrofibre': 'metrofibre',
    'metro fibre': 'metrofibre',
    'mfn': 'metrofibre',
    'frogfoot': 'frogfoot',
    'octotel': 'octotel',
    'openserve': 'openserve',
    'seacom': 'seacom',
    'century': 'centurycity',
    'centurycity': 'centurycity',
    'open fibre': 'openfibre',
    'openfibre': 'openfibre',
    'lightwire': 'lightwire'
  };
  
  // Check for direct mappings first
  for (const [key, value] of Object.entries(directMappings)) {
    if (normalizedKey.includes(key)) {
      return networkOperatorMap[value];
    }
  }
  
  // If no direct mapping, try the network operator map
  for (const [key, value] of Object.entries(networkOperatorMap)) {
    if (normalizedKey.includes(key)) {
      return value;
    }
  }
  
  console.log('No mapping found for operator:', mapKey);
  
  // Default fallback
  return {
    name: mapKey,
    logo: 'https://apextech.group/wondernet-logo-white.png',
    color: '#8B5CF6',
    description: 'High-speed internet provider'
  };
};

// Get recommended packages for a network operator
export const getRecommendedPackages = (operatorName, connectionType = 'FTTH') => {
  if (!operatorName) return { packages: [], totalCount: 0 };
  
  // Normalize the operator name
  let normalizedOperatorName = operatorName.toLowerCase();
  
  try {
    // Special handling for Vumatel variants
    const isVumatelVariant = normalizedOperatorName.includes('vuma') ||
                             normalizedOperatorName.includes('vumatel');
    
    // Special handling for Openserve variants
    const isOpenserveVariant = normalizedOperatorName.includes('openserve');
    
    // Filter packages based on operator and connection type
    const filteredPackages = transformedPackages.filter(pkg => {
      const supplier = pkg.Supplier.toLowerCase();
      
      // Use the explicit connectionType field we added to the packages
      if (pkg.connectionType !== connectionType) {
        return false;
      }
      
      // For Vumatel variants, include packages from both Vumatel and Vumatel Tactical
      if (isVumatelVariant) {
        return supplier.includes('vumatel');
      }
      
      // For Openserve variants, include all Openserve packages
      if (isOpenserveVariant) {
        return supplier.includes('openserve');
      }
      
      // Handle Frogfoot Business case - if we don't have business packages, return empty
      if (normalizedOperatorName.includes('frogfoot') && connectionType === 'FTTB') {
        return false; // We don't have Frogfoot Business packages
      }
      
      // For other operators, use the standard matching
      return supplier.includes(normalizedOperatorName) ||
             normalizedOperatorName.includes(supplier);
    });
    
    // Store the total count of packages
    const totalCount = filteredPackages.length;
    
    if (totalCount === 0) {
      return { packages: [], totalCount: 0 };
    }
    
    // Sort by download speed (lowest to highest)
    const sortedBySpeed = [...filteredPackages].sort((a, b) =>
      a["Download speed (kbps)"] - b["Download speed (kbps)"]
    );
    
    // Return all packages instead of just a subset
    return {
      packages: sortedBySpeed,
      totalCount: totalCount
    };
    
  } catch (error) {
    console.error('Error filtering packages:', error);
    return { packages: [], totalCount: 0 };
  }
};